import React, {
  useRef,
  useState,
  RefObject,
  useEffect,
  forwardRef,
  useImperativeHandle,
  ForwardRefRenderFunction,
} from 'react';
import { Tab, Tabs } from '@mui/material';
import { Typography } from '@mui/material';

import DataSpecificFilter, {
  Ref as DataSpecificFilterRef,
} from './DataSpecificFilter';
import DataCustomFilter, {
  Ref as DataCustomFilterRef,
} from './DataCustomFilter';

import UserManager from 'utils/UserManager';
import { CustomGranularityFilter } from '../helper';

interface Props {
  selectedValuesSpecific?: string[];
  selectedValuesCustom?: CustomGranularityFilter;
}

enum TabsTitle {
  CUSTOM = 'Custom',
  SPECIFIC = 'Specific',
}

export interface Ref {
  handleShow: (show: boolean) => void;
  customFilter: RefObject<DataCustomFilterRef>;
  specificFilter: RefObject<DataSpecificFilterRef>;
}

const DataSpecificAndCustomFilter: ForwardRefRenderFunction<Ref, Props> = (
  { selectedValuesCustom, selectedValuesSpecific },
  ref
) => {
  var userProperties = UserManager.getProperties();

  const customFilter = useRef<DataCustomFilterRef>(null);
  const specificFilter = useRef<DataSpecificFilterRef>(null);

  const [showComponent, setShowComponent] = useState<boolean>(false);
  const [selectedTab, setSelectedTab] = useState<TabsTitle>(TabsTitle.CUSTOM);

  const handleChange = (event: React.SyntheticEvent, newValue: TabsTitle) =>
    setSelectedTab(newValue);

  const handleShow = (show: boolean) => setShowComponent(show);

  useImperativeHandle(ref, () => ({
    handleShow,
    customFilter,
    specificFilter,
  }));

  useEffect(() => {
    if (selectedValuesSpecific) {
      setSelectedTab(TabsTitle.SPECIFIC);
    }
    if (selectedValuesCustom) {
      setSelectedTab(TabsTitle.CUSTOM);
    }
  }, [selectedValuesSpecific, selectedValuesCustom]);

  if (!showComponent) return <React.Fragment />;

  return (
    <React.Fragment>
      <Tabs
        value={selectedTab}
        onChange={handleChange}
        className="granularity-tabs-container"
        classes={{
          indicator: 'granularity-indicator-container',
        }}
        variant="fullWidth"
      >
        <Tab
          value={TabsTitle.CUSTOM}
          label={TabsTitle.CUSTOM}
          className="granularity-tab-item"
          key={`${TabsTitle.CUSTOM}-tab`}
        />
        <Tab
          value={TabsTitle.SPECIFIC}
          label={TabsTitle.SPECIFIC}
          className="granularity-tab-item"
          key={`${TabsTitle.SPECIFIC}-tab`}
        />
      </Tabs>
      <div className="granaularity-tab-content-container">
        <Typography className="granularity-custom-specific-timezone">
          Timezone: {userProperties['timeZone']}
        </Typography>
        {selectedTab === TabsTitle.SPECIFIC && (
          <DataSpecificFilter
            ref={specificFilter}
            selectedValuesSpecific={selectedValuesSpecific}
          />
        )}
        {selectedTab === TabsTitle.CUSTOM && (
          <DataCustomFilter
            ref={customFilter}
            selectedValuesCustom={selectedValuesCustom}
          />
        )}
      </div>
    </React.Fragment>
  );
};

export default forwardRef<Ref, Props>(DataSpecificAndCustomFilter);
